import type { RouteObject } from "react-router-dom";
import { RouteGuard } from "@/components/RouteGuard";
import type { AppRouteDef } from "./types";

/**
 * Translate a portal's data-authored route tree (US-044 AC7) into react-router
 * `RouteObject`s for `useRoutes`. Each renderable route is wrapped in
 * `<RouteGuard>` so its declarative `access` is enforced uniformly; Suspense for
 * the lazy page chunks comes from the portal layout's `<Outlet>` boundary
 * (Phase 3), so we don't wrap per-route here.
 */
export function buildRouteTree(routes: AppRouteDef[]): RouteObject[] {
  return routes.map((def): RouteObject => {
    const element = renderElement(def);

    if (def.index) {
      return { index: true, element };
    }

    return {
      path: def.path,
      element,
      children: def.children ? buildRouteTree(def.children) : undefined,
    };
  });
}

function renderElement(def: AppRouteDef): React.ReactNode {
  // A (typically lazy) page component — always guarded.
  if (def.Component) {
    const Component = def.Component;
    return (
      <RouteGuard access={def.access}>
        <Component />
      </RouteGuard>
    );
  }

  // A raw element (e.g. <Navigate>, or `null` for SettingsPage inline tabs).
  // Guard only if an access rule is declared; otherwise pass it through
  // verbatim to preserve behaviour (incl. `element={null}`).
  if (def.element !== undefined) {
    return def.access ? (
      <RouteGuard access={def.access}>{def.element}</RouteGuard>
    ) : (
      def.element
    );
  }

  // Pathless parent that only groups children — render the default <Outlet/>.
  return undefined;
}
